import React from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Table } from 'react-bootstrap';
import { getActivities, getHealthRange } from '../api/trainer';
import { Activity } from '../api/types';
import DistanceVolumeChart from '../components/DistanceVolumeChart';
import PageHeader from '../components/PageHeader';
import StatTile from '../components/StatTile';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { activityTypeLabel, formatDate, formatDistance, formatDuration, formatNumber, formatPace, isoDaysAgo } from '../utils/format';

/** Fetches every page of activities since `from` (capped, so a bad backend cannot loop forever). */
async function activitiesSince(from: string): Promise<Activity[]> {
  const all: Activity[] = [];
  for (let page = 0; page < 5; page++) {
    const result = await getActivities({ from, page, size: 100 });
    all.push(...result.items);
    if (page + 1 >= result.totalPages) break;
  }
  return all;
}

/** Last row of an oldest-first series, if any. */
const latest = <T,>(list: T[] | null | undefined): T | undefined => (list && list.length ? list[list.length - 1] : undefined);

const DashboardPage: React.FC = () => {
  const recent = useAsync(() => activitiesSince(isoDaysAgo(28)), []);
  const health = useAsync(async () => {
    const range = { from: isoDaysAgo(14), to: isoDaysAgo(0) };
    const [hrv, sleep, daily, battery] = await Promise.all([
      getHealthRange('hrv', range),
      getHealthRange('sleep', range),
      getHealthRange('daily', range),
      getHealthRange('body-battery', range),
    ]);
    return { hrv, sleep, daily, battery };
  }, []);

  const activities = recent.data ?? [];
  const lastHrv = latest(health.data?.hrv);
  const lastSleep = latest(health.data?.sleep);
  const lastDaily = latest(health.data?.daily);
  const lastBattery = latest(health.data?.battery);

  return (
    <>
      <PageHeader title="Dashboard">
        <p>Last four weeks of training and your recovery signals.</p>
      </PageHeader>

      <Row className="g-3 mb-3">
        <Col xs={6} md={4} xl={2}>
          <StatTile
            to="/health/hrv"
            label="HRV last night"
            value={<>{formatNumber(lastHrv?.lastNightAvg)} ms</>}
            hint={<>7-day avg {formatNumber(lastHrv?.weeklyAvg)} ms</>}
          />
        </Col>
        <Col xs={6} md={4} xl={2}>
          <StatTile
            to="/health/resting-hr"
            label="Resting HR"
            value={<>{formatNumber(lastDaily?.restingHr)} bpm</>}
          />
        </Col>
        <Col xs={6} md={4} xl={2}>
          <StatTile
            to="/health/sleep"
            label="Sleep score"
            value={formatNumber(lastSleep?.sleepScore)}
            hint={<>{formatDuration(lastSleep?.totalSleepS)} asleep</>}
          />
        </Col>
        <Col xs={6} md={4} xl={2}>
          <StatTile
            to="/health/body-battery"
            label="Body battery high"
            value={formatNumber(lastBattery?.highest)}
            hint={<>Low {formatNumber(lastBattery?.lowest)}</>}
          />
        </Col>
        <Col xs={6} md={4} xl={2}>
          <StatTile to="/health/stress" label="Stress" value={formatNumber(lastDaily?.stressAvg)} hint="Daily average" />
        </Col>
        <Col xs={6} md={4} xl={2}>
          <StatTile to="/health/steps" label="Steps" value={formatNumber(lastDaily?.steps)} />
        </Col>
      </Row>

      <StatusMessage loading={health.loading} error={health.error} onRetry={health.reload} />

      <Row className="g-3 mb-3">
        <Col xs={12}>
          <DistanceVolumeChart />
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12}>
          <section className="surface h-100">
            <h2>Recent activities</h2>
            <StatusMessage loading={recent.loading} error={recent.error} onRetry={recent.reload} />
            {!recent.loading && !recent.error && activities.length === 0 && (
              <p className="muted mb-0">No activities in the last four weeks.</p>
            )}
            {activities.length > 0 && (
              <div className="table-responsive">
                <Table size="sm" className="trainer-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Activity</th>
                      <th className="text-end">Distance</th>
                      <th className="text-end">Pace</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.slice(0, 5).map(a => (
                      <tr key={a.id}>
                        <td className="tabular">{formatDate(a.startTime)}</td>
                        <td>
                          <Link to={`/activities/${a.id}`}>{a.name ?? 'Untitled activity'}</Link>
                          <div className="stat-hint">{activityTypeLabel(a.activityType)}</div>
                        </td>
                        <td className="text-end tabular">{formatDistance(a.distanceM)}</td>
                        <td className="text-end tabular">{formatPace(a.avgPaceSPerKm)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </section>
        </Col>
      </Row>
    </>
  );
};

export default DashboardPage;
